import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { Can, ReactTable, useCan } from "components";
import { useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { AddUpdateTyreBrandDiscountModal, AddUpdateTyreDiscountModal, DeleteTyreDiscountModal, TyreBrandDiscountsTable } from "modules/TyreDiscountsDashboard/components";
import { useCategoriesQuery, useDiscountsQuery, useTyreBrandsQuery } from "modules/TyreDiscountsDashboard/components/TyreDiscountsTable/hooks";
import "modules/TyreDiscountsDashboard/components/TyreDiscountsTable/TyreDiscountsTable.scss";
import { PAGE_SIZE } from "utils/common";

import type { Row } from "@tanstack/react-table";
import type { TyreDiscount } from "models";

const columnHelper = createColumnHelper<TyreDiscount>();

export const TyreDiscountsTable = () => {
  const { t } = useTranslation();

  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const [deleteTyreDiscountId, setDeleteTyreDiscountId] = useState(0);
  const [addBrandTyreDiscountId, setBrandTyreDiscountId] = useState(0);
  const [updateTyreDiscount, setUpdateTyreDiscount] = useState<TyreDiscount | null>(null);

  const { data: brandOptions, isLoading: isLoadingTyreBrands } = useTyreBrandsQuery();
  const { data: discounts, isLoading: isLoadingDiscounts, refetch: refetchDiscounts } = useDiscountsQuery();
  const { data: categoriesOptions, isLoading: isLoadingCategories, refetch: refetchCategories } = useCategoriesQuery();

  const canListTyreDiscounts = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.TyreDiscounts);
  const canListTyreDiscountCategories = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.TyreDiscountCategories);

  useRegisterNavAction(() => {
    if (canListTyreDiscounts) refetchDiscounts();
    if (canListTyreDiscountCategories) refetchCategories();
  });

  const handleToggleExpanded = (e: React.MouseEvent<HTMLButtonElement>, toggleExpanded: (expanded?: boolean) => void) => {
    e.stopPropagation();
    toggleExpanded();
  };

  const handleCollapseRow = (rowId: string) =>
    setExpandedRows(prev => {
      const { [rowId]: _, ...rest } = prev;
      return rest;
    });

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "name",
        header: () => <span className="no-wrap-text">{t("v8_name", "Name")}</span>,
        size: 100,
        cell: ({
          row: {
            original: { active, tyre_brand_discounts, name },
            toggleExpanded,
            getIsExpanded
          }
        }) => (
          <div className="table-name-container">
            <div className={`${active ? "active-discount" : "inactive-discount"}`} />

            <span className="no-wrap-text">
              {Boolean(tyre_brand_discounts?.length) && (
                <button className="table-expand-button" onClick={e => handleToggleExpanded(e, toggleExpanded)}>
                  {getIsExpanded() ? <Icon className="caret down" /> : <Icon className="caret right" />}
                </button>
              )}
              {name}
            </span>
          </div>
        )
      }),
      columnHelper.accessor(row => row, {
        id: "brand",
        header: () => <span className="no-wrap-text">{t("v8_brand", "Brand")}</span>,
        size: 80,
        cell: () => <span className="no-wrap-text">{t("v8_default", "Default")}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "category",
        header: () => <span className="no-wrap-text">{t("v8_category", "Category")}</span>,
        size: 80,
        cell: () => <span className="no-wrap-text">{t("v8_default", "Default")}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "discount_on_price",
        header: () => <span className="no-wrap-text">{t("v8_discount_on_price", "Discount (On Price)")}</span>,
        size: 130,
        cell: ({
          row: {
            original: { discount }
          }
        }) => <span className="no-wrap-text"> {discount ? `${discount}%` : ""} </span>
      }),
      columnHelper.accessor(row => row, {
        id: "net_margin",
        header: () => <span className="no-wrap-text">{`${t("v8_net", "Net")} + ${t("v8_margin", "Margin")}`}</span>,
        size: 110,
        cell: ({
          row: {
            original: { net_margin_euro }
          }
        }) => <span className="no-wrap-text"> {net_margin_euro ? net_margin_euro.toFixed(2) : ""} </span>
      }),
      columnHelper.accessor(row => row, {
        id: "net_percent",
        header: () => <span className="no-wrap-text">{`${t("v8_net", "Net")} + ${t("v8_percent", "Percent")}`}</span>,
        size: 120,
        cell: ({
          row: {
            original: { net_margin_percent }
          }
        }) => <span className="no-wrap-text"> {net_margin_percent ? `${net_margin_percent}%` : ""} </span>
      }),
      columnHelper.accessor(row => row, {
        id: "actions",
        size: 50,
        header: () => "",
        cell: ({ row: { original } }) => (
          <div className="actions-container">
            <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.TyreDiscountBrands}>
              <div className="action-add-button-container" onClick={() => setBrandTyreDiscountId(original.id)}>
                <Icon className="plus action-button-icon-center" />
              </div>
            </Can>
            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.TyreDiscounts}>
              <div className="action-edit-button-container" onClick={() => setUpdateTyreDiscount(original)}>
                <Icon className="icon pen to square edit-icon action-button-icon-center" color="#32bf53" />
              </div>
            </Can>
            <Can I={ACL_ACTIVITIES.Delete} the={ACL_RESOURCES.TyreDiscounts}>
              <div className="action-button-container" onClick={() => setDeleteTyreDiscountId(original.id)}>
                <Icon className="trash action-button-icon-center" color="red" />
              </div>
            </Can>
          </div>
        )
      })
    ],
    [t, handleToggleExpanded]
  );

  return (
    <>
      <div className="TyreDiscountsTable">
        <ReactTable
          isFetching={isLoadingDiscounts || isLoadingCategories || isLoadingTyreBrands}
          data={discounts}
          columns={columns}
          pageSize={PAGE_SIZE.PageSize25}
          expanded={expandedRows}
          onExpandedChange={setExpandedRows}
          renderExpandedComponent={(row: Row<TyreDiscount>) => (
            <TyreBrandDiscountsTable
              brandOptions={brandOptions || []}
              brandDiscounts={row.original.tyre_brand_discounts}
              categoriesOptions={categoriesOptions || []}
              discounts={discounts}
              onCollapseRow={handleCollapseRow}
            />
          )}
        />
      </div>
      <AddUpdateTyreBrandDiscountModal
        brandOptions={brandOptions || []}
        tyreBrandDiscount={null}
        categoriesOptions={categoriesOptions || []}
        tyreBrandTyreDiscountId={addBrandTyreDiscountId}
        onClose={() => setBrandTyreDiscountId(0)}
      />
      <AddUpdateTyreDiscountModal isOpen={Boolean(updateTyreDiscount)} onClose={() => setUpdateTyreDiscount(null)} tyreDiscount={updateTyreDiscount} />
      <DeleteTyreDiscountModal id={deleteTyreDiscountId} onResetDeleteTyreDiscountId={() => setDeleteTyreDiscountId(0)} />
    </>
  );
};
